import { Frame, HexColor, OFF, PIXEL_COUNT, emptyFrame } from './led-board.model';

/**
 * Live sessions are switched off while the shared canvas is sorted out. The
 * code stays in place; flip this to true (and pass --sessions on the Pi) to
 * bring the feature back.
 */
export const SESSIONS_ENABLED = false;

/** Session lengths offered when someone queues one, in minutes. */
export const SESSION_LENGTHS_MIN = [2, 3, 5];
export const DEFAULT_SESSION_MS = 3 * 60 * 1000;

/** Written by the board when a session's turn starts, at /ledboard/session/meta. */
export interface SessionMeta {
  /** The queue key the session came from. */
  id: string;
  startedAt: number;
  endsAt: number;
  durationMs: number;
}

/** One person drawing, at /ledboard/session/people/<clientId>. Anonymous by design. */
export interface SessionPerson {
  /** Their dot and cursor colour — the only thing identifying them. */
  color: string;
  /** Where they last painted, as a pixel index, so others can see them working. */
  cell?: number;
  at: number;
}

/**
 * Cursor colours, in order. No names anywhere in a session, so a colour is the
 * only way to tell people apart; these are picked to stay distinct on black.
 */
export const PERSON_COLORS = [
  '#ff4d6d', '#ffd60a', '#4ade80', '#bf5af2', '#ff9500',
  '#30d5c8', '#5cc8fa', '#ff6fae', '#a3e635', '#f97316',
];

/** Give a client a colour that stays the same for as long as they are drawing. */
export function colorForClient(clientId: string): string {
  let hash = 0;
  for (let i = 0; i < clientId.length; i++) { hash = (hash * 31 + clientId.charCodeAt(i)) >>> 0; }
  return PERSON_COLORS[hash % PERSON_COLORS.length];
}

/** A random id for this browser, so presence can be cleaned up when it leaves. */
export function newClientId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

export function sessionRemainingMs(meta: SessionMeta | null, at: number): number {
  if (!meta) { return 0; }
  return Math.max(0, meta.endsAt - at);
}

export function sessionIsLive(meta: SessionMeta | null, at: number): boolean {
  return !!meta && at < meta.endsAt;
}

/** "2:04" — the countdown everyone in the session is watching. */
export function countdownText(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
}

/**
 * Turn the shared pixel map into a frame. The map holds only the pixels people
 * have painted, keyed by index as a string, so everything else stays off.
 */
export function frameFromPixels(pixels: Record<string, HexColor> | null | undefined, base?: Frame): Frame {
  const frame = base ? base.slice() : emptyFrame();
  if (!pixels) { return frame; }
  for (const key of Object.keys(pixels)) {
    const i = Number(key);
    if (Number.isInteger(i) && i >= 0 && i < PIXEL_COUNT) {
      frame[i] = pixels[key] || OFF;
    }
  }
  return frame;
}

/**
 * The pixels that changed between two frames, as the shared map wants them.
 * Painting sends only this, so several phones can draw at once without any of
 * them overwriting the others' work with a whole-canvas write.
 */
export function pixelDiff(before: Frame, after: Frame): Record<string, HexColor> {
  const out: Record<string, HexColor> = {};
  for (let i = 0; i < after.length; i++) {
    if (before[i] !== after[i]) { out[String(i)] = after[i]; }
  }
  return out;
}
